import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import API from "../api";
import { Check, X, Store, Package, Euro, ShoppingCart, FileText, Building2, Phone, Mail, BadgeCheck, MessageSquare } from "lucide-react";

const AdminDashboard = () => {
  const { tl } = useApp();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [proAccounts, setProAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);

  const load = () => {
    API.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    API.get("/admin/vendors?status=pending").then((r) => setVendors(r.data)).catch(() => {});
    API.get("/admin/products?status=pending").then((r) => setPendingProducts(r.data)).catch(() => {});
    API.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    API.get("/admin/pro-accounts?status=pending").then((r) => setProAccounts(r.data)).catch(() => {});
    API.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!user || user.role !== "admin") return null;

  const setVendorStatus = async (id, status) => { await API.post(`/admin/vendors/${id}/status`, { status }); load(); };
  const setProductStatus = async (id, status) => { await API.post(`/admin/products/${id}/status`, { status }); load(); };
  const setQuoteStatus = async (id, status) => { await API.post(`/admin/quotes/${id}/status`, { status }); load(); };
  const setProStatus = async (id, status) => { await API.post(`/admin/pro-accounts/${id}/status`, { status }); load(); };
  const setContactStatus = async (id, status) => { await API.post(`/admin/contacts/${id}/status`, { status }); load(); };

  const cards = stats ? [
    { icon: ShoppingCart, label: "Commandes", value: stats.total_orders },
    { icon: Euro, label: "Chiffre d'affaires", value: `${stats.revenue.toFixed(2)} €` },
    { icon: Euro, label: "Commissions (10%)", value: `${stats.commission_total.toFixed(2)} €` },
    { icon: Package, label: "Produits", value: stats.total_products },
    { icon: FileText, label: "Devis B2B", value: stats.new_quotes ?? 0 },
    { icon: BadgeCheck, label: "Comptes pro", value: stats.pending_pro ?? 0 },
    { icon: MessageSquare, label: "Messages", value: stats.new_contacts ?? 0 },
  ] : [];

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-10 fade-up">
      <div className="h-px w-10 bg-[#b08d57] mb-4" />
      <h1 className="font-serif-lux text-4xl mb-8">Administration</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
        {cards.map((c, i) => (
          <div key={i} className="bg-white border border-border rounded-sm p-5">
            <c.icon className="h-5 w-5 text-[#b08d57] mb-3" strokeWidth={1.5} />
            <p className="text-2xl font-serif-lux">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <section className="mb-12">
        <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-2"><Store className="h-5 w-5 text-[#b08d57]" strokeWidth={1.5} /> Vendeurs en attente ({vendors.length})</h2>
        {vendors.length === 0 ? <p className="text-sm text-muted-foreground">Aucun vendeur en attente.</p> : (
          <div className="space-y-3">
            {vendors.map((v) => (
              <div key={v.id} className="flex items-center justify-between border border-border rounded-sm p-4 bg-white">
                <div>
                  <p className="font-medium">{v.shop_name}</p>
                  <p className="text-xs text-muted-foreground">{v.name} · {v.email}</p>
                  {v.shop_desc && <p className="text-xs text-[#555] mt-1">{v.shop_desc}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setVendorStatus(v.id, "approved")} className="flex items-center gap-1 bg-[#2f7d4f] text-white px-3 py-2 text-xs rounded-sm hover:opacity-90"><Check className="h-4 w-4" /> Approuver</button>
                  <button onClick={() => setVendorStatus(v.id, "rejected")} className="flex items-center gap-1 border border-[#8b1e3f] text-[#8b1e3f] px-3 py-2 text-xs rounded-sm hover:bg-[#8b1e3f] hover:text-white transition-colors"><X className="h-4 w-4" /> Rejeter</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-2"><Package className="h-5 w-5 text-[#b08d57]" strokeWidth={1.5} /> Produits en attente ({pendingProducts.length})</h2>
        {pendingProducts.length === 0 ? <p className="text-sm text-muted-foreground">Aucun produit en attente.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingProducts.map((p) => (
              <div key={p.id} className="border border-border rounded-sm bg-white overflow-hidden">
                <img src={p.image} alt="" className="w-full aspect-[3/4] object-cover" />
                <div className="p-3">
                  <p className="text-xs text-muted-foreground">{p.vendor_name}</p>
                  <p className="text-sm line-clamp-1">{tl(p.name)}</p>
                  <p className="text-sm font-medium mb-2">{p.price} €</p>
                  <div className="flex gap-2">
                    <button onClick={() => setProductStatus(p.id, "approved")} className="flex-1 flex items-center justify-center gap-1 bg-[#2f7d4f] text-white px-2 py-2 text-xs rounded-sm hover:opacity-90"><Check className="h-4 w-4" /> Approuver</button>
                    <button onClick={() => setProductStatus(p.id, "rejected")} className="flex-1 flex items-center justify-center gap-1 border border-[#8b1e3f] text-[#8b1e3f] px-2 py-2 text-xs rounded-sm hover:bg-[#8b1e3f] hover:text-white transition-colors"><X className="h-4 w-4" /> Rejeter</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-[#b08d57]" strokeWidth={1.5} /> Comptes professionnels en attente ({proAccounts.length})</h2>
        {proAccounts.length === 0 ? <p className="text-sm text-muted-foreground">Aucun compte professionnel en attente.</p> : (
          <div className="space-y-3" data-testid="admin-pro-accounts">
            {proAccounts.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-border rounded-sm p-4 bg-white">
                <div>
                  <p className="font-medium">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.email}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setProStatus(a.id, "approved")} data-testid={`pro-approve-${a.id}`} className="flex items-center gap-1 bg-[#2f7d4f] text-white px-3 py-2 text-xs rounded-sm hover:opacity-90"><Check className="h-4 w-4" /> Valider</button>
                  <button onClick={() => setProStatus(a.id, "rejected")} className="flex items-center gap-1 border border-[#8b1e3f] text-[#8b1e3f] px-3 py-2 text-xs rounded-sm hover:bg-[#8b1e3f] hover:text-white transition-colors"><X className="h-4 w-4" /> Rejeter</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-2"><FileText className="h-5 w-5 text-[#b08d57]" strokeWidth={1.5} /> Demandes de devis Négoce ({quotes.length})</h2>
        {quotes.length === 0 ? <p className="text-sm text-muted-foreground">Aucune demande de devis.</p> : (
          <div className="space-y-3" data-testid="admin-quotes">
            {quotes.map((q) => (
              <div key={q.id} className="border border-border rounded-sm p-4 bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-[#b08d57]" strokeWidth={1.5} /> {q.company}</p>
                    <p className="text-xs text-muted-foreground mt-1">{q.contact_name}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-[#555]">
                      <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {q.email}</span>
                      {q.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {q.phone}</span>}
                    </div>
                    {q.product_name && <p className="text-sm text-[#1b1b1b] mt-2">{q.product_name} — <span className="font-medium">{q.quantity} unités</span></p>}
                    {q.message && <p className="text-xs text-[#555] mt-1 italic">« {q.message} »</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-sm ${q.status === "new" ? "bg-[#b08d57] text-white" : q.status === "handled" ? "bg-[#2f7d4f] text-white" : "bg-gray-200 text-[#555]"}`}>{q.status === "new" ? "Nouveau" : q.status === "handled" ? "Traité" : q.status}</span>
                    {q.status === "new" && <button onClick={() => setQuoteStatus(q.id, "handled")} className="flex items-center gap-1 bg-[#2f7d4f] text-white px-3 py-1.5 text-xs rounded-sm hover:opacity-90"><Check className="h-3.5 w-3.5" /> Marquer traité</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#b08d57]" strokeWidth={1.5} /> Messages de contact ({contacts.length})</h2>
        {contacts.length === 0 ? <p className="text-sm text-muted-foreground">Aucun message pour le moment.</p> : (
          <div className="space-y-3" data-testid="admin-contacts">
            {contacts.map((m) => (
              <div key={m.id} className="border border-border rounded-sm p-4 bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium">{m.name} {m.subject && <span className="text-muted-foreground font-normal">— {m.subject}</span>}</p>
                    <a href={`mailto:${m.email}`} className="text-xs text-[#b08d57] hover:underline flex items-center gap-1 mt-1"><Mail className="h-3.5 w-3.5" /> {m.email}</a>
                    <p className="text-sm text-[#555] mt-2 whitespace-pre-wrap">{m.message}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-sm ${m.status === "new" ? "bg-[#b08d57] text-white" : "bg-[#2f7d4f] text-white"}`}>{m.status === "new" ? "Nouveau" : "Traité"}</span>
                    {m.status === "new" && <button onClick={() => setContactStatus(m.id, "handled")} className="flex items-center gap-1 bg-[#2f7d4f] text-white px-3 py-1.5 text-xs rounded-sm hover:opacity-90"><Check className="h-3.5 w-3.5" /> Marquer traité</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

    </div>
  );
};

export default AdminDashboard;
